import {
  checkIcon,
  darkArrowRightIcon,
  homePageIcon,
  lightbulbIcon,
  logoIconV2,
  portalIcon,
  signOutIcon,
} from '@/shared/ui/assets';
import { BIOFLUX_ROLE, EMITTER_CONSTANTS, LIST_ROLES_CALL_CENTER } from '@/shared/config';
import { auth, emitter, generateFullName } from '@/shared/lib';
import { Button, Popover, Typography } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { NavLink } from 'react-router-dom';
import { t } from 'ttag';
import { LANGUAGES } from '../../config/sidebar';
import { getDisplayedLanguageName } from '../../lib/sidebar';
import { useSidebar } from './useSidebar';
import Avatar from './components/avatar/avatar';
import LanguageIcon from './components/languageIcon/languageIcon';
import TabIcon from './components/tabIcon/tabIcon';
import NotificationsCenter from '../notificationCenter/notificationsCenter';
import type { SidebarProps } from './sidebar.types';
import './sidebar.scss';

/**
 * Application sidebar holding the logo, the main navigation tabs and the bottom notification, what's-new and avatar controls. Maps the menu prop to NavLinks iconed by TabIcon, emits DISPLAY_WHAT_IS_NEW_MODAL from the what's-new button and badges it when the tutorial was deferred, and opens an avatar popover offering the language list, other-portal and Biocare links plus sign out, all backed by useSidebar state.
 *
 * @param menu - The navigation entries to render, each with an id, link and name.
 * @returns The sidebar element.
 */
const Sidebar = ({ menu }: SidebarProps) => {
  const {
    state,
    me,
    rmaTicketCountRedux,
    biocareButtonList,
    onClickErpHomePage,
    onClickSignOut,
    onClickLanguage,
    handleOpenChange,
    gotoOtherPortal,
  } = useSidebar();

  const willViewTutorialLater = JSON.parse(auth.getWillViewTutorialLater());

  return (
    <aside
      id="scroll-wrapper"
      className="custom-sidebar --small-padding-top"
      aria-label={t`Main navigation`}
    >
      <div className="custom-sidebar__container">
        <div className="custom-sidebar__top">
          <div className="custom-sidebar__logo">
            <img className="logoIcon" src={logoIconV2} alt="Logo" />
          </div>
          <div className="sidebar-hr-line" />
          <div className="menu-icon-container">
            {_.map(menu, (item) => (
              <NavLink
                key={item.id}
                to={item.link}
                {...({ activeClassName: 'active' } as any)}
                className="sidebar-tab"
              >
                <div className="sidebar-tab-content">
                  <TabIcon
                    iconLink={item.link}
                    iconName={item.name}
                    pathname={state.pathname}
                    rmaTicketCount={rmaTicketCountRedux}
                  />
                  <span className="sidebar-tab-content__name">{item.name}</span>
                </div>
              </NavLink>
            ))}
          </div>
        </div>

        <div className="custom-sidebar__bottom">
          <NotificationsCenter />

          <div className="whats-new-icon-container">
            <Button
              className="whats-new-btn"
              aria-label={t`What's new`}
              onClick={() => {
                emitter.emit(EMITTER_CONSTANTS.DISPLAY_WHAT_IS_NEW_MODAL, {
                  isOpenWhatNewPopup: true,
                });
              }}
            >
              <img
                className="whats-new-icon"
                src={lightbulbIcon}
                alt="lightbulbIcon"
                aria-hidden="true"
              />
              {willViewTutorialLater && <div className="whats-new-badge" />}
            </Button>
          </div>

          <div className="icons-container">
            <div className="avatar-container">
              <Popover
                open={state.openAvatarPopover}
                onOpenChange={handleOpenChange}
                classNames={{ root: 'avatar-popover' }}
                trigger="click"
                arrow={false}
                placement="rightBottom"
                content={
                  <div className="avatar-popup">
                    <div className="avatar-popup__top">
                      <Avatar
                        className="avatar-img"
                        firstName={state.userInfo.firstName}
                        lastName={state.userInfo.lastName}
                        avatarLink={me.avatarLink}
                      />
                      <div className="user-info">
                        <Typography.Text
                          style={{
                            maxWidth: 188,
                            fontSize: 18,
                            fontWeight: 500,
                          }}
                          ellipsis={{
                            tooltip: <div>{generateFullName(state.userInfo)}</div>,
                          }}
                        >
                          {generateFullName(state.userInfo)}
                        </Typography.Text>
                        <div className="role">
                          {state.isAdminOrSales
                            ? t`Clinic Technician`
                            : _.intersection(state.userInfo.roles, [
                                ...BIOFLUX_ROLE,
                                ...LIST_ROLES_CALL_CENTER,
                              ])[0]}
                        </div>
                      </div>
                    </div>
                    <div className="list-btn">
                      <Popover
                        classNames={{ root: 'lang-popover' }}
                        placement="right"
                        arrow={false}
                        // oxlint-disable-next-line react/no-unstable-nested-components
                        content={_.map(LANGUAGES, (language) => (
                          <div
                            className={classNames({
                              'lang-active': language.code === state.selectedLanguage,
                            })}
                          >
                            <Button
                              className="lang-btn"
                              block
                              type="text"
                              key={language.code}
                              onClick={() => {
                                onClickLanguage(language.code);
                              }}
                            >
                              <LanguageIcon languageCode={language.code} />
                              <div>{language.name}</div>
                              {language.code === state.selectedLanguage && (
                                <img src={checkIcon} alt="checkIcon" />
                              )}
                            </Button>
                          </div>
                        ))}
                      >
                        <Button block type="text" className="lang-btn">
                          <LanguageIcon languageCode={state.selectedLanguage} />
                          <div className="language">
                            {getDisplayedLanguageName(state.selectedLanguage)}
                          </div>
                          <img src={darkArrowRightIcon} alt={t`Dark right arrow icon`} />
                        </Button>
                      </Popover>
                      {state.isLoginWithMicrosoftAccount && (
                        <>
                          {state.displayedPortals.length > 0 && (
                            <Popover
                              classNames={{ root: 'portal-popover' }}
                              placement="rightTop"
                              arrow={false}
                              // oxlint-disable-next-line react/no-unstable-nested-components
                              content={_.map(state.displayedPortals, (portal) => (
                                <Button
                                  className="portal-btn"
                                  type="text"
                                  block
                                  key={portal.name}
                                  onClick={() => gotoOtherPortal(portal)}
                                >
                                  {portal.name}
                                </Button>
                              ))}
                            >
                              <Button block type="text" className="lang-btn">
                                <img src={portalIcon} alt="portal" />
                                <div className="language">{t`Other Portals`}</div>
                                <img src={darkArrowRightIcon} alt={t`Dark right arrow icon`} />
                              </Button>
                            </Popover>
                          )}
                          <Button
                            type="text"
                            block
                            className="list-biocare-portal"
                            onClick={onClickErpHomePage}
                          >
                            <img src={homePageIcon} alt="homePageIcon" />
                            <div className="text">{t`ERP Home Page`}</div>
                          </Button>
                        </>
                      )}
                      {auth.isLoginWithBiocarePortal() &&
                        _.map(biocareButtonList, (portal) => (
                          <Button
                            type="text"
                            block
                            className="list-biocare-portal"
                            onClick={() => {
                              handleOpenChange(false);
                              window.open(portal.link ?? undefined);
                            }}
                          >
                            {portal.icon}
                            <div className="text">{portal.name}</div>
                          </Button>
                        ))}
                      <Button
                        type="text"
                        block
                        className="list-biocare-portal"
                        onClick={() => {
                          onClickSignOut();
                        }}
                      >
                        <img src={signOutIcon} alt={t`Sign out icon`} />
                        <div className="text">{t`Sign out`}</div>
                      </Button>
                    </div>
                  </div>
                }
              >
                <Avatar
                  className="avatar-btn"
                  firstName={state.userInfo.firstName}
                  lastName={state.userInfo.lastName}
                  avatarLink={me.avatarLink}
                />
              </Popover>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
